import React, { useEffect } from 'react';
import styled from 'styled-components';
import {
  Alert,
  FlexColumn,
  Flex,
  Button,
  LoadingButton,
} from '@elementary/common-ui/components';
import { Typography, Dialog, SxProps } from '@mui/material';
import { DataHealthError } from '../../types';

const Content = styled(FlexColumn)`
  gap: 32px;
  padding: 24px;
`;

const Footer = styled(Flex)`
  padding: 24px;
  border-top: 1px solid ${({ theme }) => theme.palette.common.gray8};
  justify-content: space-between;
  align-items: center;
`;

const Link = styled.a`
  color: ${({ theme }) => theme.palette.common.blue4};
`;

export const DataHealthErrorAlert = ({ error }: { error: DataHealthError }) => {
  const { title, text } = error.dialogError
    ? { title: error.message, text: error.dialogError }
    : { title: undefined, text: error.message };

  return (
    <Alert severity="error" title={title}>
      <span>{text}</span>
    </Alert>
  );
};

export const parseDataHealthError = (
  error: any,
): DataHealthError | undefined => {
  if (!error) {
    return undefined;
  }
  const responseData = error?.response?.data;
  if (responseData?.elementary_error_code != null) {
    return {
      message: 'Connection error',
      dialogError: (responseData?.extra?.integration_error as string) || null,
    };
  }

  if (responseData?.detail != null) {
    const message = (responseData.detail || [])
      .map((item: { msg?: string }) => item?.msg)
      .join(' - ');

    return { message };
  }

  return undefined;
};

type Props = {
  open: boolean;
  title: string;
  description?: string;
  instructionBox?: React.ReactNode;
  children: React.ReactNode;
  onClose: () => void;
  onSubmit?: (values: any) => void;
  isValid: boolean;
  docsLink?: string;
  error?: DataHealthError;
  reset?: () => void;
  loading?: boolean;
  width?: string;
  sx?: SxProps;
  hideSaveButton?: boolean;
  saveButtonText?: string;
};

const DataHealthDialog = ({
  open,
  title,
  description,
  instructionBox,
  children,
  onClose,
  onSubmit,
  isValid,
  docsLink,
  error,
  reset,
  loading,
  width,
  sx,
  hideSaveButton,
  saveButtonText,
}: Props) => {
  useEffect(() => {
    if (open) {
      reset?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog
      sx={{ '& .MuiDialog-paper': { width: width || '870px' }, ...sx }}
      open={open}
      maxWidth={false}
    >
      <form onSubmit={onSubmit}>
        <Content>
          <FlexColumn sx={{ gap: 2 }}>
            <Typography sx={{ fontWeight: 'bold' }}>{title}</Typography>
            {description && (
              <Typography>
                {description}{' '}
                {docsLink && (
                  <>
                    Check our{' '}
                    <Link href={docsLink} target="_blank">
                      docs
                    </Link>{' '}
                    for more info.
                  </>
                )}
              </Typography>
            )}
            {instructionBox}
          </FlexColumn>
          {children}
          {error && <DataHealthErrorAlert error={error} />}
        </Content>

        <Footer>
          <Flex sx={{ gap: 1, margin: '0 0 0 auto' }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={onClose}
            >
              Close
            </Button>
            {!hideSaveButton && (
              <LoadingButton
                type="submit"
                size="large"
                disabled={!isValid}
                loading={loading}
              >
                {saveButtonText || (loading ? 'Saving' : 'Save')}
              </LoadingButton>
            )}
          </Flex>
        </Footer>
      </form>
    </Dialog>
  );
};

export default DataHealthDialog;
